/**
 * One customer: how often they buy, what they have spent after refunds, what
 * they owe and have repaid, and everything they bought.
 *
 * Which medicines somebody bought is clinical information. The server names
 * those lines only for staff who may read patient records; anyone else sees
 * that a medicine was bought, and the money, but not which.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Lock } from 'lucide-react';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, type Customer, type Paginated, type PaymentMethod } from '@cenox/shared';
import { ApiError, api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { useModules } from '../../lib/modules';
import { TRANSFER_REFERENCE_HINT, needsConfirmation, needsTransferReference, transferReferenceReady } from '../../lib/till';
import { count, date, money, todayIn } from '../../lib/format';
import { Button, Card, EmptyState, ErrorState, Input, Modal, Pagination, Select, TableSkeleton } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { SegmentBadge } from './CustomersPage';

interface PurchaseLine {
  description: string;
  quantity: string;
  lineTotal: string;
  hidden: boolean;
}
interface Purchase {
  saleId: string;
  reference: string;
  occurredAt: string;
  amount: string;
  refunded: string;
  net: string;
  outstanding: string;
  paymentStatus: string;
  counted: boolean;
  lines: PurchaseLine[];
}
interface Debt {
  kind: 'sale' | 'opening_balance';
  id: string;
  reference: string | null;
  date: string;
  amount: string;
  settled: string;
  outstanding: string;
}
interface Repayment {
  kind: 'sale_payment' | 'opening_balance_payment';
  id: string;
  againstReference: string | null;
  amount: string;
  method: string;
  paidAt: string;
}

export function CustomerDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const timezone = business?.timezone ?? 'UTC';
  const currency = business?.currencyCode ?? 'USD';
  const canSeeSales = can('sales:read');
  const { active } = useModules();

  const customer = useQuery({
    queryKey: ['customers', business?.businessId, 'one', id],
    queryFn: () => api.get<Customer>(path(`/customers/${id}`)),
    enabled: Boolean(business && id),
  });

  if (customer.isPending) return <TableSkeleton columns={4} />;
  if (customer.error || !customer.data) {
    return <ErrorState message="Could not load this customer." onRetry={() => customer.refetch()} />;
  }
  const c = customer.data;

  return (
    <>
      <Link to="/customers" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" /> Customers
      </Link>
      <PageHeader title={c.name} description={[c.phone, c.email].filter(Boolean).join(' · ') || 'No contact details'} />
      <div className="mb-4">
        <SegmentBadge segment={c.segment} />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Stat label="Purchases" value={count(c.transactionCount)} hint={`${count(c.purchasesLast90Days)} in 90 days`} />
        <Stat label="Lifetime spending" value={money(c.totalSpent, currency)} hint="After refunds" />
        <Stat label="Average purchase" value={c.averagePurchase ? money(c.averagePurchase, currency) : '—'} />
        <Stat label="First purchase" value={date(c.firstPurchaseAt, timezone)} />
        <Stat label="Last purchase" value={date(c.lastPurchaseAt, timezone)} />
        <Stat
          label="Owes"
          value={money(c.totalOutstanding, currency)}
          hint={Number(c.openingBalanceOutstanding) > 0 ? `incl. ${money(c.openingBalanceOutstanding, currency)} opening balance` : undefined}
          warn={Number(c.totalOutstanding) > 0}
        />
      </div>

      {active.has('pharmacy') && <LoyaltyCard id={id} currency={currency} timezone={timezone} />}

      {canSeeSales ? (
        <>
          <DebtsCard id={id} currency={currency} timezone={timezone} />
          <PurchasesCard id={id} currency={currency} timezone={timezone} />
        </>
      ) : (
        <Card>
          <p className="text-sm text-slate-600">Purchase history and debts need permission to read sales.</p>
        </Card>
      )}
    </>
  );
}

function Stat({ label, value, hint, warn }: { label: string; value: string; hint?: string | undefined; warn?: boolean }): ReactNode {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold tabular ${warn ? 'text-amber-700' : 'text-slate-900'}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function DebtsCard({ id, currency, timezone }: { id: string; currency: string; timezone: string }): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const [paying, setPaying] = useState<Debt | null>(null);
  const q = useQuery({
    queryKey: ['customers', business?.businessId, 'debts', id],
    queryFn: () => api.get<{ debts: Debt[]; repayments: Repayment[] }>(path(`/customers/${id}/debts`)),
  });
  if (q.isPending) return null;
  if (q.error || !q.data) return <ErrorState message="Could not load debts." onRetry={() => q.refetch()} />;
  const { debts, repayments } = q.data;
  return (
    <div className="mb-4 grid gap-4 lg:grid-cols-2">
      <Card padded={false}>
        <h2 className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Outstanding</h2>
        {debts.length === 0 ? (
          <p className="px-4 py-3 text-sm text-slate-500">Nothing owed.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {debts.map((d) => (
              <li key={`${d.kind}-${d.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <span className="text-slate-700">
                  {d.kind === 'opening_balance' ? 'Opening balance' : d.reference} · {date(d.date, timezone)}
                  <span className="block text-xs text-slate-500">
                    {money(d.settled, currency)} paid of {money(d.amount, currency)}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="font-medium tabular text-amber-700">{money(d.outstanding, currency)}</span>
                  {d.kind === 'opening_balance' && can('payments:create') && (
                    <Button size="sm" variant="secondary" onClick={() => setPaying(d)}>
                      Record payment
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {paying && (
        <RepaymentModal customerId={id} debt={paying} currency={currency} timezone={timezone} onClose={() => setPaying(null)} />
      )}
      <Card padded={false}>
        <h2 className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Repayments</h2>
        {repayments.length === 0 ? (
          <p className="px-4 py-3 text-sm text-slate-500">No repayments recorded.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {repayments.map((r) => (
              <li key={`${r.kind}-${r.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <span className="text-slate-700">
                  {date(r.paidAt, timezone)} · {r.method.replace(/_/g, ' ')}
                  <span className="block text-xs text-slate-500">
                    {r.kind === 'opening_balance_payment' ? 'Against the opening balance' : `Against ${r.againstReference}`}
                  </span>
                </span>
                <span className="font-medium tabular text-emerald-700">{money(r.amount, currency)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const LOYALTY_KIND: Record<string, string> = {
  earn: 'Earned',
  earn_reversal: 'Taken back',
  redeem: 'Spent',
  redeem_restore: 'Given back',
};

/** Loyalty points (pharmacy, 0159): shown once the customer has any, or while points are on. */
function LoyaltyCard({ id, currency, timezone }: { id: string; currency: string; timezone: string }): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const q = useQuery({
    queryKey: ['pharmacy', business?.businessId, 'loyalty', id],
    queryFn: () =>
      api.get<{
        balance: number;
        balanceValue: string;
        earningEnabled: boolean;
        redemptionEnabled: boolean;
        entries: Array<{ id: string; kind: string; points: number; note: string | null; at: string; saleReference: string | null }>;
      }>(path(`/pharmacy/loyalty/customers/${id}`)),
  });
  const l = q.data;
  if (!l || (!l.entries.length && !l.earningEnabled && !l.redemptionEnabled)) return null;
  return (
    <Card padded={false} className="mb-4">
      <h2 className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">
        Loyalty points: {count(l.balance)}
        {l.balance > 0 && <span className="font-normal text-slate-500"> · worth {money(l.balanceValue, currency)}</span>}
      </h2>
      {l.entries.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-500">No points yet. Points are earned on paid non-medicines.</p>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {l.entries.slice(0, 20).map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3 px-4 py-2">
              <span className="text-slate-700">
                {LOYALTY_KIND[e.kind] ?? e.kind}
                {e.saleReference && <span className="text-slate-500"> · {e.saleReference}</span>}
                <span className="block text-xs text-slate-500">{date(e.at, timezone)}{e.note ? ` · ${e.note}` : ''}</span>
              </span>
              <span className={e.points > 0 ? 'tabular-nums text-emerald-700' : 'tabular-nums text-slate-700'}>
                {e.points > 0 ? `+${e.points}` : e.points}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function PurchasesCard({ id, currency, timezone }: { id: string; currency: string; timezone: string }): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const [page, setPage] = useState(1);
  const filters = qs({ page, pageSize: 20 });
  const q = useQuery({
    queryKey: ['customers', business?.businessId, 'purchases', id, filters],
    queryFn: () => api.get<Paginated<Purchase>>(path(`/customers/${id}/purchases${filters}`)),
  });
  return (
    <Card padded={false}>
      <h2 className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">Purchase history</h2>
      {q.isPending ? (
        <TableSkeleton columns={4} />
      ) : q.error || !q.data ? (
        <div className="p-4">
          <ErrorState message="Could not load purchases." onRetry={() => q.refetch()} />
        </div>
      ) : q.data.data.length === 0 ? (
        <EmptyState title="No purchases yet" description="Sales linked to this customer appear here." />
      ) : (
        <>
          <ul className="divide-y divide-slate-100">
            {q.data.data.map((p) => (
              <li key={p.saleId} className={`px-4 py-3 text-sm ${p.counted ? '' : 'bg-slate-50'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">
                      {p.reference} <span className="font-normal text-slate-500">· {date(p.occurredAt, timezone)}</span>
                    </p>
                    {!p.counted && <p className="text-xs text-slate-500">Fully refunded — not counted as a purchase</p>}
                  </div>
                  <div className="text-right tabular">
                    <p className="font-semibold text-slate-900">{money(p.net, currency)}</p>
                    {Number(p.refunded) > 0 && (
                      <p className="text-xs text-slate-500">
                        {money(p.amount, currency)} less {money(p.refunded, currency)} refunded
                      </p>
                    )}
                    {Number(p.outstanding) > 0 && <p className="text-xs font-medium text-amber-700">{money(p.outstanding, currency)} owed</p>}
                  </div>
                </div>
                {p.lines.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                    {p.lines.map((l, i) => (
                      <li key={i} className="flex justify-between gap-3">
                        <span className={l.hidden ? 'inline-flex items-center gap-1 italic text-slate-500' : ''}>
                          {l.hidden && <Lock className="size-3" aria-hidden />}
                          {l.hidden ? 'Medicine — visible to pharmacists' : l.description} × {Number(l.quantity)}
                        </span>
                        <span className="tabular">{money(l.lineTotal, currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPageChange={setPage} />
        </>
      )}
    </Card>
  );
}

const RECEIVED_METHODS = PAYMENT_METHODS.filter((m) => m !== 'credit');

/**
 * A payment against a balance owed from before the business moved to CenoX.
 * Money in on the day it arrived — never a sale. One per attempt, however
 * often the button is pressed.
 */
function RepaymentModal({
  customerId,
  debt,
  currency,
  timezone,
  onClose,
}: {
  customerId: string;
  debt: Debt;
  currency: string;
  timezone: string;
  onClose: () => void;
}): ReactNode {
  const path = useBusinessPath();
  const client = useQueryClient();
  const [amount, setAmount] = useState(debt.outstanding);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [paidOn, setPaidOn] = useState(todayIn(timezone));
  const [reference, setReference] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  // In a pharmacy, money other than cash counts once seen; a transfer names its alert's reference.
  const pharmacy = useModules().active.has('pharmacy');
  const confirmPayment = pharmacy && needsConfirmation(method);
  const referenceMissing = pharmacy && needsTransferReference(method) && !transferReferenceReady(reference);
  const { branchId: working, defaultBranchId, locations } = useWorkingLocation();
  const [branchId, setBranchId] = useState(working ?? defaultBranchId ?? locations[0]?.id ?? '');
  // One attempt per set of details: retrying the same payment keeps its key,
  // changing what is paid starts a new one, so a lost response cannot pass off
  // an earlier payment as this one.
  const [attempt, setAttempt] = useState(() => `ob-${crypto.randomUUID()}`);
  useEffect(() => setAttempt(`ob-${crypto.randomUUID()}`), [amount, method, paidOn, branchId]);
  const save = useMutation({
    mutationFn: () =>
      api.post(path(`/customers/${customerId}/opening-balances/${debt.id}/payments`), {
        amount,
        method,
        paidOn,
        ...(branchId ? { branchId } : {}),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(confirmPayment ? { confirmed } : {}),
        idempotencyKey: attempt,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
  });
  const tooMuch = Number(amount) > Number(debt.outstanding);
  return (
    <Modal open onClose={onClose} title="Record a payment of an old debt">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Carried over from before CenoX: {money(debt.outstanding, currency)} still owed. This is money received, not a sale.
        </p>
        <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} {...(tooMuch ? { error: 'More than is owed' } : {})} />
        <Select
          label="Paid by"
          value={method}
          onChange={(e) => {
            setMethod(e.target.value as PaymentMethod);
            setConfirmed(false);
          }}
          options={RECEIVED_METHODS.map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
        />
        <Input label="Date received" type="date" value={paidOn} max={todayIn(timezone)} onChange={(e) => setPaidOn(e.target.value)} />
        {locations.length > 1 && (
          <Select
            label="Received at"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            options={locations.map((l) => ({ value: l.id, label: l.name }))}
          />
        )}
        <Input
          label={pharmacy && needsTransferReference(method) ? 'Transfer reference' : 'Reference (optional)'}
          hint={pharmacy && needsTransferReference(method) ? TRANSFER_REFERENCE_HINT : undefined}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
        {confirmPayment && (
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" className="mt-1" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            I have seen the approval (terminal slip / bank alert)
          </label>
        )}
        {save.error && (
          <p role="alert" className="text-sm text-red-600">
            {save.error instanceof ApiError ? save.error.message : 'The payment could not be recorded.'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={save.isPending} disabled={!(Number(amount) > 0) || tooMuch || (confirmPayment && !confirmed) || referenceMissing} onClick={() => save.mutate()}>
            Record payment
          </Button>
        </div>
      </div>
    </Modal>
  );
}
